import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default:
          'rounded-md bg-primary text-primary-foreground shadow-sm shadow-black/5 ring-1 ring-inset ring-white/10 hover:opacity-92 active:opacity-88 py-4 px-8',
        gradient:
          'rounded-md bg-cta-gradient text-primary-foreground shadow-sm shadow-black/5 hover:opacity-95 py-4 px-8',
        destructive:
          'rounded-md bg-destructive text-white shadow-none hover:bg-destructive/90 focus-visible:ring-destructive/25',
        outline:
          'rounded-md bg-transparent text-foreground shadow-none ring-1 ring-[color-mix(in_srgb,var(--on-surface)_15%,transparent)] hover:bg-accent',
        secondary:
          'rounded-md bg-secondary text-secondary-foreground shadow-none hover:bg-secondary/80',
        ghost:
          'rounded-md hover:bg-accent hover:text-accent-foreground shadow-none',
        link: 'text-primary underline-offset-4 hover:underline shadow-none',
        success:
          'rounded-md bg-[var(--on-tertiary-fixed-variant)] text-[var(--tertiary-fixed)] shadow-none hover:opacity-90 py-4 px-8',
      },
      size: {
        default: '',
        sm: 'h-8 rounded-md gap-1.5 px-3 text-xs has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
